import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { z } from "zod";
import { useLogin } from "../../hooks/useAuth";
import { AuthShell } from "../components/auth/AuthShell";
import { SocialAuthButtons } from "../components/auth/SocialAuthButtons";
import { EmailField, FormAlert, PasswordField, SubmitButton } from "../components/auth/fields";
import { authErrorKind, authErrorText } from "../components/auth/authErrors";

type LoginForm = { email: string; password: string };

export function Login() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? "";

  // Built per language so validation messages follow a language switch.
  const schema = useMemo(
    () =>
      z.object({
        email: z.string().trim().min(1, t("authFlow.emailRequired", "Введите email")).pipe(
          z.email(t("createListing.login.emailInvalid", "Неверный адрес электронной почты")),
        ),
        password: z.string().min(1, t("authFlow.passwordRequired", "Введите пароль")),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(schema), defaultValues: { email: prefillEmail } });

  const login = useLogin();
  const kind = login.isError ? authErrorKind(login.error) : null;

  const onSubmit = (data: LoginForm) => login.mutate(data);

  return (
    <AuthShell title={t("createListing.login.title")} subtitle={t("createListing.login.subtitle")}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-3 auth-tall:gap-4">
        <EmailField
          label={t("createListing.login.email")}
          placeholder="email@example.com"
          enterKeyHint="next"
          error={errors.email?.message}
          {...register("email")}
        />

        <PasswordField
          label={t("createListing.login.password")}
          placeholder={t("createListing.login.passwordPlaceholder")}
          autoComplete="current-password"
          enterKeyHint="go"
          autoFocus={!!prefillEmail}
          error={errors.password?.message}
          action={
            <Link
              to="/forgot-password"
              state={{ email: getValues("email"), from: (location.state as { from?: unknown } | null)?.from }}
              className="text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              {t("createListing.login.forgot")}
            </Link>
          }
          {...register("password")}
        />

        {kind && (
          <FormAlert>
            {authErrorText(t, kind, t("authFlow.errors.unknown"))}
            {kind === "notFound" && (
              <>
                {" "}
                <Link
                  to="/register"
                  state={{ email: getValues("email"), from: (location.state as { from?: unknown } | null)?.from }}
                  className="font-semibold underline"
                >
                  {t("authFlow.createWithThisEmail", "Создать аккаунт")}
                </Link>
              </>
            )}
            {kind === "wrongPassword" && (
              <>
                {" "}
                <Link
                  to="/forgot-password"
                  state={{ email: getValues("email"), from: (location.state as { from?: unknown } | null)?.from }}
                  className="font-semibold underline"
                >
                  {t("authFlow.resetIt", "Восстановить пароль")}
                </Link>
              </>
            )}
          </FormAlert>
        )}

        <SubmitButton busy={login.isPending}>{t("createListing.login.signIn")}</SubmitButton>
      </form>

      <SocialAuthButtons />

      <p className="mt-4 text-center text-sm text-gray-500 auth-tall:mt-6 auth-tall:text-[15px]">
        {t("createListing.login.noAccount")}{" "}
        <Link
          to="/register"
          state={location.state}
          className="font-bold text-blue-600 hover:text-blue-700"
        >
          {t("createListing.login.signUp")}
        </Link>
      </p>
    </AuthShell>
  );
}
